import Promise from 'bluebird';
import pixelPromise from 'pixel-promise';
import createPagination from 'giant-piano';

import metrics from 'metrics';
import { log } from 'logger';
import { hashToUserQueryId } from '@seek/seek-jobs-analytics';
import { searchResultsViewModel } from '@seek/seek-refine-job-search';
import { normaliseSearchQuery } from '@seek/seek-jobs-seo';
import createApiClient from 'seek-jobs-api-client';
import uniq from 'lodash.uniq';

import { LOCATION_CHANGED } from 'store/location/location';

const api = createApiClient();

const paginate = createPagination({ itemsPerPage: 20, maxPages: 7 });

export const MORE_OPTIONS = 'MORE_OPTIONS';
export const GET_RESULTS_BEGIN = 'GET_RESULTS_BEGIN';
export const CLEAR_RESULTS = 'CLEAR_RESULTS';
export const GET_RESULTS_SUCCESS = 'GET_RESULTS_SUCCESS';
const GET_RESULTS_ERROR = 'GET_RESULTS_ERROR';
export const RESULTS_PAGE_LOAD = 'RESULTS_PAGE_LOAD';
const RESULT_LINK_CLICKED = 'RESULT_LINK_CLICKED';
const JORA_RESULT_LINK_CLICKED = 'JORA_RESULT_LINK_CLICKED';

const initialState = {
  results: null,
  location: null,
  locationWhere: null,
  title: '',
  totalCount: null,
  jobIds: [],
  pagination: null,
  paginationParameters: {},
  sortMode: null,
  hidden: false
};

const calculateResults = ({ query = {}, results = null, location, totalCount, suggestions, isError = false }) => {
  const page = query.page || '1';

  const viewModel = searchResultsViewModel({
    page, results, location, totalCount, suggestions,
    searchParams: query
  });
  const jobIds = results ? results.map(job => job.id) : [];

  return {
    results: {
      ...viewModel,
      suburbs: results ? uniq(results.map(job => job.suburbWhereValue).filter(Boolean)) : [],
      isError
    },
    jobIds
  };
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case MORE_OPTIONS: {
      return {
        ...state,
        hidden: true
      };
    }

    case CLEAR_RESULTS:
    case GET_RESULTS_BEGIN: {
      return {
        ...state,
        results: {
          ...state.results,
          suburbs: [],
          isError: false,
          isLoading: true
        },
        location: {},
        hidden: false
      };
    }

    case LOCATION_CHANGED: {
      return {
        ...state,
        title: ''
      };
    }

    case GET_RESULTS_SUCCESS: {
      const {
        query,
        currentPageNumber,
        userQueryId
      } = action.payload;

      const {
        title,
        data,
        totalCount,
        location = null,
        suggestions,
        paginationParameters = {},
        sortMode = null
      } = action.payload.result;

      const locationWhere = (
        location === null ||
        location.description === 'All Australia' ||
        location.description === 'All New Zealand'
      ) ? '' : location.description;
      const pagination =
        totalCount === 0 ? null : paginate({ currentPage: currentPageNumber, totalItems: totalCount });

      return {
        ...state,
        ...calculateResults({ query, results: data, location, totalCount, suggestions }),
        location,
        locationWhere,
        title,
        totalCount,
        userQueryId,
        pagination,
        paginationParameters,
        sortMode
      };
    }

    case GET_RESULTS_ERROR: {
      return {
        ...state,
        ...calculateResults({ isError: true }),
        title: 'Error loading results',
        pagination: null
      };
    }

    default: {
      return state;
    }
  }
}

export const resultPageLoad = () => {
  return {
    type: RESULTS_PAGE_LOAD
  };
};

export const moreOptions = () => ({
  type: MORE_OPTIONS,
  meta: {
    hotjar: 'More Options Clicked'
  }
});

export const getResults = ({ locale, path, query, paginationParameters = {}, cookies }) => (dispatch, getState) => {
  const normalisedQuery = normaliseSearchQuery({ path, query });
  const apiQuery = { ...normalisedQuery, ...paginationParameters, sourcesystem: 'houston' };
  const prevQueryId = getState().results.userQueryId;
  const userQueryId = hashToUserQueryId({ query: normalisedQuery, prevQueryId });

  dispatch({ type: GET_RESULTS_BEGIN, payload: { query: normalisedQuery } });

  return api.jobs.search(apiQuery, locale, cookies, userQueryId)
    .timeout(2000)
    .catch(Promise.TimeoutError, () => {
      metrics.count('search-api.timeout-retry', ['timeout-retry:true']);
      log.info({ query }, 'Search api timeout retry');
      return api.jobs.search(apiQuery, locale, cookies, userQueryId);
    })
    .then(result => {
      const { totalCount } = result;

      const currentPageNumber = getState().location.pageNumber;
      const zeroResults = (totalCount === 0);
      const hotjarTags = zeroResults ? ['Zero Results'] : [];

      dispatch({
        type: GET_RESULTS_SUCCESS,
        payload: { query: normalisedQuery, result, currentPageNumber, userQueryId },
        meta: {
          metrics: {
            name: GET_RESULTS_SUCCESS,
            tags: { zeroResults }
          },
          hotjar: hotjarTags
        }
      });

      const url = getState().location.url;
      log.info({ url, totalCount }, 'Search results');
    })
    .catch(Promise.CancellationError, () => { /* Swallow cancellations */ })
    .catch(err => {
      dispatch({ type: GET_RESULTS_ERROR, error: true, payload: err });
      throw err; // This is a critical app error so we need to bubble it up
    });
};

export const resultLinkClicked = job => dispatch => {
  const { joraClickTrackingUrl, id } = job;

  dispatch({
    type: RESULT_LINK_CLICKED,
    meta: {
      metrics: {
        name: RESULT_LINK_CLICKED,
        tags: { id }
      }
    }
  });

  if (joraClickTrackingUrl) {
    dispatch({
      type: JORA_RESULT_LINK_CLICKED,
      meta: {
        metrics: {
          name: JORA_RESULT_LINK_CLICKED,
          tags: { id, joraClickTrackingUrl }
        }
      }
    });

    return pixelPromise(joraClickTrackingUrl);
  }
  return null;
};

export const clearResults = () => ({
  type: CLEAR_RESULTS
});

export const jobsSelector = state => {
  const { jobs, tiers } = state.results.results;

  if (jobs !== null) { // jobs is null when we have tiered results
    return jobs;
  }

  return Array.prototype.concat.apply([], tiers.map(tier => tier.jobs));
};



/** WEBPACK FOOTER **
 ** ./app/store/results/results.js
 **/